import { useEffect, useRef, useState } from 'react';

import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { usePageDataContext } from '@ulta/core/providers/PageDataProvider/PageDataProvider';

import * as utils from './useStickToHeader';

/** useStickToHeader custome hook to return headeroffset value
 * @param {object} data - Arguments
 * @param {object} data.positionElementRef - positionElementRef Ref
 */
export const useStickToHeader = ( data ) => {
  const { positionElementRef } = data || {};
  const { displayOverlay } = useOverlay();
  const { headerOffset } = usePageDataContext();
  // Ref that manages the start position
  const positionTop = useRef( 0 );
  // Flag for "enabling" should offset the anchor list
  const [shouldOffsetElement, setShouldOffsetElement] = useState( false );

  // Manages anchors sticky position interaction w/ header's hide/show animation
  useEffect(
    utils.stickyElement(
      { displayOverlay, positionTop, positionElementRef, shouldOffsetElement },
      { setShouldOffsetElement }
    ),
    [displayOverlay, positionTop, positionElementRef, shouldOffsetElement, setShouldOffsetElement]
  );
  return [shouldOffsetElement, headerOffset];
};
/**
 * We need to manage the state of the offset for anchors.
 *
 * This is required because if we just added header offset, once you scroll back up past the anchor list, it
 * would still offset itself past it's own "sticky" point in the DOM.
 *
 * There is content between the header and the anchor list, so the point where it sticks will be at a different
 * scrollY than when the header sticks (scrollY=0).
 *
 * 1. When we're scrolled past the anchors we "enable" offset.
 * 2. For the animations to be perform we need to know exactly where the anchors started
 * so we track that on resize and use a conditional prop based on "enable offset" flag `shouldOffsetElement`
 *
 * @param {object} data - Arguments
 * @param {boolean} data.displayOverlay - Is overlay open
 * @param {object} data.positionTop - positionTop Ref
 * @param {object} data.positionElementRef - positionElementRef Ref
 * @param {boolean} data.shouldOffsetElement - Flag to set true/false
 * @param {object} methods - Methods
 * @param {function} methods.setShouldOffsetElement - setShouldOffsetElement method
 */
export const stickyElement = ( data, methods ) => () => {
  const { displayOverlay, positionTop = {}, positionElementRef = {}, shouldOffsetElement } = data || {};
  const { setShouldOffsetElement } = methods || {};

  if( !setShouldOffsetElement || displayOverlay ){
    return;
  }

  const onScroll = () => {
    const isPastOffsetElement = global.scrollY > positionTop.current;

    // If we're below the anchor list, we need to set a flag to enable
    // offsetting based on the header
    if( isPastOffsetElement && !shouldOffsetElement ){
      setShouldOffsetElement( true );
    }
    // When we're above the anchor list, we can disable responding to the header
    // offset
    else if( !isPastOffsetElement && shouldOffsetElement ){
      setShouldOffsetElement( false );
    }
  };

  // Maintain the top position of the anchor list. We need to do this on resize
  // because there are responsive images that change the vertical height of content
  // above the anchor list
  const onResize = () => {
    const { top = 0 } = positionElementRef.current?.getBoundingClientRect?.() || {};
    positionTop.current = Math.floor( global.scrollY + top );
  };

  onResize();

  global.addEventListener( 'scroll', onScroll );
  global.addEventListener( 'resize', onResize );

  return () => {
    global.removeEventListener( 'scroll', onScroll );
    global.removeEventListener( 'resize', onResize );
  };
};
